import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { ShieldAlert, AlertTriangle, CheckCircle } from 'lucide-react';

/**
 * Try-Catch Configuration Component
 * 
 * Provides an intuitive UI for configuring error handling with:
 * - Try block (risky operations)
 * - Catch block (error handling)
 * - Finally block (cleanup)
 * - Error variable configuration
 */
const TryCatchConfig = ({ config, onChange }) => {
  const [errorVariable, setErrorVariable] = useState(config?.errorVariable || 'caught_error');
  const [rethrow, setRethrow] = useState(config?.rethrow || false);
  const [catchErrorTypes, setCatchErrorTypes] = useState(config?.catchErrorTypes || ['all']);

  const errorTypes = [
    { value: 'all', label: 'All Errors', description: 'Catch any error that occurs' },
    { value: 'network', label: 'Network Errors', description: 'Connection, timeout, DNS issues' },
    { value: 'auth', label: 'Authentication Errors', description: 'Unauthorized, forbidden' },
    { value: 'validation', label: 'Validation Errors', description: 'Invalid data, schema errors' },
    { value: 'rate_limit', label: 'Rate Limit Errors', description: 'Too many requests' },
    { value: 'timeout', label: 'Timeout Errors', description: 'Operation took too long' },
    { value: 'server_error', label: 'Server Errors', description: '500, 502, 503 errors' },
  ];

  const updateConfig = (updates) => {
    onChange({
      ...config,
      ...updates
    });
  };

  const toggleErrorType = (type) => {
    let updated;
    if (type === 'all') {
      updated = ['all'];
    } else {
      updated = catchErrorTypes.filter(t => t !== 'all');
      if (updated.includes(type)) {
        updated = updated.filter(t => t !== type);
      } else {
        updated = [...updated, type];
      }
      if (updated.length === 0) {
        updated = ['all'];
      }
    }
    setCatchErrorTypes(updated);
    updateConfig({ catchErrorTypes: updated });
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldAlert className="w-5 h-5 text-red-500" />
          <h3 className="text-lg font-semibold">Try-Catch Configuration</h3>
        </div>
        <Badge variant="outline" className="bg-red-500/10 text-red-600 border-red-500/30">
          Error Handler
        </Badge>
      </div>

      {/* Overview Card */}
      <Card className="border-blue-500/20 bg-blue-500/5">
        <CardContent className="pt-4">
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center text-white flex-shrink-0">
                <CheckCircle className="w-4 h-4" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium">Try Block</p>
                <p className="text-xs text-muted-foreground">
                  Contains the nodes that might fail. If any node fails, execution jumps to the catch block.
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-red-500 flex items-center justify-center text-white flex-shrink-0">
                <AlertTriangle className="w-4 h-4" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium">Catch Block</p>
                <p className="text-xs text-muted-foreground">
                  Handles errors from the try block. Error details are available in a variable.
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-gray-500 flex items-center justify-center text-white flex-shrink-0">
                <CheckCircle className="w-4 h-4" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium">Finally Block (Optional)</p>
                <p className="text-xs text-muted-foreground">
                  Always executes, whether an error occurred or not. Good for cleanup operations.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error Variable */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Error Variable</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Input
            placeholder="caught_error"
            value={errorVariable}
            onChange={(e) => {
              setErrorVariable(e.target.value);
              updateConfig({ errorVariable: e.target.value });
            }}
            className="font-mono text-sm"
          />
          <p className="text-xs text-muted-foreground">
            Access error details in catch block using {`{{${errorVariable}.message}}`} or {`{{${errorVariable}.type}}`}
          </p>
          <div className="mt-3 p-3 bg-muted rounded-lg">
            <p className="text-xs font-medium mb-2">Error Object Structure:</p>
            <pre className="text-xs font-mono text-muted-foreground">
{`{
  "message": "Error description",
  "type": "NetworkError",
  "timestamp": "2024-01-15T10:30:00Z"
}`}
            </pre>
          </div>
        </CardContent>
      </Card>

      {/* Error Types to Catch */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Error Types to Catch</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {errorTypes.map((errorType) => (
            <div
              key={errorType.value}
              className={`
                p-3 rounded-lg border-2 cursor-pointer transition-all
                ${catchErrorTypes.includes(errorType.value)
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/50'
                }
              `}
              onClick={() => toggleErrorType(errorType.value)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium">{errorType.label}</p>
                    {catchErrorTypes.includes(errorType.value) && (
                      <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30 text-xs">
                        Active
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {errorType.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
          <p className="text-xs text-muted-foreground mt-3">
            Click to select which error types this catch block should handle
          </p>
        </CardContent>
      </Card>

      {/* Advanced Options */}
      <Card className="border-orange-500/20">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Advanced Options</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Re-throw Error After Catch</Label>
              <p className="text-xs text-muted-foreground">
                Execute catch block, then propagate the error to parent workflow
              </p>
            </div>
            <Switch
              checked={rethrow}
              onCheckedChange={(checked) => {
                setRethrow(checked);
                updateConfig({ rethrow: checked });
              }}
            />
          </div>
        </CardContent>
      </Card>

      {/* Configuration Summary */}
      <Card className="bg-red-500/5 border-red-500/20">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Configuration Summary</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <p>
            <span className="text-muted-foreground">Error Variable:</span>{' '}
            <span className="font-mono text-xs">{errorVariable}</span>
          </p>
          <p>
            <span className="text-muted-foreground">Catching:</span>{' '}
            {catchErrorTypes.includes('all') ? (
              <Badge variant="outline" className="bg-red-500/10 text-red-600">All Errors</Badge>
            ) : (
              <span className="text-xs">
                {catchErrorTypes.map(type => 
                  errorTypes.find(et => et.value === type)?.label
                ).join(', ')}
              </span>
            )}
          </p>
          <p>
            <span className="text-muted-foreground">Re-throw:</span>{' '}
            <Badge variant="outline" className={rethrow ? 'bg-orange-500/10 text-orange-600' : 'bg-green-500/10 text-green-600'}>
              {rethrow ? 'Yes' : 'No'}
            </Badge>
          </p>
        </CardContent>
      </Card>

      {/* Usage Example */}
      <Card className="bg-muted/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Usage Example</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-xs">
            <p className="text-muted-foreground">
              <strong>Try Block:</strong> Call external API, process data
            </p>
            <p className="text-muted-foreground">
              <strong>Catch Block:</strong> Send error notification, log to database
            </p>
            <p className="text-muted-foreground">
              <strong>Finally Block:</strong> Clean up temporary files, close connections
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TryCatchConfig;
